import React, { useMemo } from "react";
import PropTypes from "prop-types";
import SimpleBar from "simplebar-react";
import "simplebar/src/simplebar.css";
import hexToRgb from "assets/theme/hex-to-rgb";

// react component for creating dynamic tables
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";

// @material-ui/core components
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";

import PrintButton from "components/_refactor/button/print";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import NotFound from "components/Texts/NotFound";
import UtilsFncs from "utils/UtilsFncs";

const useStyles = makeStyles((theme) => ({
  tableActions: {
    justifyContent: "flex-end",
    right: 0,
    position: "absolute",
  },
  actionsWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginRight: 16,
    "& > .search-label": {
      marginRight: 10,
      width: "80%",
      marginBottom: 0,
    },
  },
  footerClass: {
    position: "absolute",
    "& > th": {
      background: "none",
      border: "none",
    },
  },
  tableWrapper: {
    paddingBottom: 20,
    "& > .react-bootstrap-table-pagination": {
      marginTop: 35,
    },
  },
  tableClass: {
    "& > tfoot": {
      height: 40,
      minHeight: "fit-content",
    },
  },
  scrollbarClass: {
    "& > .simplebar-wrapper": {
      paddingBottom: "1rem",
    },
    "& > .simplebar-track > .simplebar-scrollbar::before": {
      backgroundColor: `rgba(${hexToRgb(theme.palette.black.main)}, 0.5)`,
    },
    "&.simplebar-dragging .simplebar-track > .simplebar-scrollbar::before": {
      backgroundColor: `rgba(${hexToRgb(theme.palette.black.main)}, 0.9)`,
    },
  },
}));

const customTotal = (from, to, size) => (
  <span
    className="react-bootstrap-table-pagination-total"
    style={{ marginLeft: "1rem" }}
  >
    Exibindo {from} até {to} de {size} resultados
  </span>
);

const getPaginationCmp = (
  { options, defaultOption, ...pagination },
  dataLength,
  selectRef
) => {
  return paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: false,
    withFirstAndLast: false,
    sizePerPage: defaultOption || 25,
    nextPageTitle: "próxima página",
    prePageTitle: "página anterior",
    paginationTotalRenderer: customTotal,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className={"dataTables_length"} id="datatable-basic_length">
        <label>
          Mostrar{" "}
          {
            <select
              ref={selectRef}
              name="datatable-basic_length"
              aria-controls="datatable-basic"
              className="form-control form-control-sm"
              onClick={(e) => {
                if (!e.isTrusted) {
                  onSizePerPageChange(e.target.value);
                }
              }}
              onChange={(e) => onSizePerPageChange(e.target.value)}
              defaultValue={defaultOption}
            >
              {[
                ...options?.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                )),
                <option key={"all"} hidden={true} value={dataLength}>
                  Todos
                </option>,
              ]}
            </select>
          }{" "}
          registros.
        </label>
      </div>
    ),
    ...pagination,
  });
};

const ToolkitTableHeader = ({
  headerTitle,
  csvFileName,
  csvProps,
  showPrint,
  showSearch,
  selectRef,
  componentRef,
  searchProps,
  dataTable,
  extraActions,
  changeSearchPlaceholder,
}) => {
  const classes = useStyles();
  const isMobile = UtilsFncs.IsMobile();

  return (
    <Grid
      container
      spacing={4}
      direction="row"
      style={{
        marginBottom: 4,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Grid item xs={isMobile ? 12 : 7}>
        <Box
          component={Typography}
          variant="h2"
          marginBottom="0!important"
          paddingLeft="24px"
        >
          <Box component="span">{headerTitle}</Box>
        </Box>
      </Grid>
      <Grid className={classes.actionsWrapper} item xs={"auto"}>
        {extraActions}
        {showSearch && (
          <SearchBar
            className="form-control-sm"
            style={{ marginLeft: "1rem" }}
            placeholder={
              changeSearchPlaceholder ? "Digite o que busca..." : "Pesquisar"
            }
            {...searchProps}
          />
        )}
        {csvFileName && (
          <IconButtonLoader
            title="Exportar CSV da página"
            onClick={csvProps.onExport}
            placement="bottom"
            extraStyle={{ marginRight: 10, marginLeft: 10 }}
            icon={
              <img
                src="https://img.icons8.com/material-outlined/24/000000/export-csv.png"
                alt="ícone clicável para exportar a tabela no formato CSV"
              />
            }
            size="small"
          />
        )}
        {showPrint && (
          <PrintButton
            selectRef={selectRef}
            componentRef={componentRef}
            dataTable={dataTable}
          />
        )}
      </Grid>
    </Grid>
  );
};

const { SearchBar } = Search;

const ToolkitTable = ({
  keyField,
  dataTable,
  columnModel,
  headerTitle,
  csvFileName,
  pagination,
  bootstrapTableProps,
  showPrint,
  CustomHeader,
  searched,
  allowEmpty,
  notFoundTitle,
  notFoundSubtitle,
  noResultTitle,
  noResultSubtitle,
  hasFooter,
  extraActions,
  searchFormatted,
  styledRow,
  changeSearchPlaceholder,
  showSearch,
  onRowClick,
}) => {
  const classes = useStyles();
  const componentRef = React.useRef(null);
  const selectRef = React.useRef(null);
  const Pagination = useMemo(
    () =>
      pagination &&
      getPaginationCmp(pagination, dataTable?.length || 0, selectRef),
    [pagination, dataTable?.length]
  );

  return (
    <>
      {alert}
      <Card>
        {searched && (
          <>
            {!dataTable || (dataTable.length === 0 && !allowEmpty) ? (
              <NotFound title={notFoundTitle} subtitle={notFoundSubtitle} />
            ) : (
              <ToolkitProvider
                data={dataTable}
                keyField={keyField}
                columns={columnModel}
                search={{ searchFormatted }}
                exportCSV={
                  csvFileName
                    ? {
                        fileName: csvFileName,
                        separator: ";",
                        blobType: "text/csv;charset=utf-8",
                        noAutoBOM: false,
                      }
                    : null
                }
              >
                {(props) => (
                  <div
                    className="py-4 table-responsive"
                    style={{ overflow: "hidden" }}
                  >
                    {CustomHeader ? (
                      <CustomHeader
                        searchProps={props.searchProps}
                        csvProps={props.csvProps}
                        componentRefProps={componentRef}
                        selectRefProps={selectRef}
                      />
                    ) : (
                      <ToolkitTableHeader
                        changeSearchPlaceholder={changeSearchPlaceholder}
                        componentRef={componentRef}
                        csvFileName={csvFileName}
                        csvProps={props.csvProps}
                        dataTable={dataTable}
                        searchProps={props.searchProps}
                        selectRef={selectRef}
                        showPrint={showPrint}
                        showSearch={showSearch}
                        headerTitle={headerTitle}
                        extraActions={extraActions}
                      />
                    )}
                    <Grid item xs={12}>
                      <SimpleBar className={classes.scrollbarClass}>
                        {searched && dataTable.length === 0 ? (
                          <NotFound
                            title={noResultTitle}
                            subtitle={noResultSubtitle}
                          />
                        ) : (
                          <BootstrapTable
                            rowStyle={styledRow}
                            rowEvents={
                              onRowClick
                                ? {
                                    onClick: onRowClick,
                                  }
                                : null
                            }
                            ref={componentRef}
                            {...props.baseProps}
                            bootstrap4={true}
                            bordered={false}
                            condensed={true}
                            striped={true}
                            hover={true}
                            footerClasses={
                              hasFooter ? classes.footerClass : undefined
                            }
                            classes={hasFooter ? classes.tableClass : undefined}
                            {...bootstrapTableProps}
                            pagination={Pagination || null}
                          />
                        )}
                      </SimpleBar>
                    </Grid>
                  </div>
                )}
              </ToolkitProvider>
            )}
          </>
        )}
      </Card>
    </>
  );
};

ToolkitTable.defaultProps = {
  searched: true,
  extraActions: null,
  searchFormatted: true,
};

ToolkitTable.propTypes = {
  /**
   * Dados que serão exibidos na tabela
   */
  dataTable: PropTypes.array,
  /**
   * Lista do modelo das colunas que serão exibidas
   */
  columnModel: PropTypes.array.isRequired,
  /**
   * Título do cabeçalho da tabela.
   */
  headerTitle: PropTypes.string,
  /**
   * Nome do arquivo CSV que poderá ser criado ao clicar no botão Exportar CSV. Se a prop não for passada, o botão não irá aparecer.
   */
  csvFileName: PropTypes.string,
  /**
   * Configuração da paginação da tabela
   */
  pagination: PropTypes.shape({
    /**
     * Opções de itens por página que sera exibido no dropdown de paginação.
     * Ex.: [25, 50, 100]
     */
    options: PropTypes.arrayOf(PropTypes.number),
    /**
     * Quantidade de itens por página que será exibido por padrão pela paginação.
     */
    defaultOption: PropTypes.number,
    /**
     * Exibe total de registros
     */
    showTotal: PropTypes.bool,
    /**
     * Define o total de registros
     */
    totalSize: PropTypes.number,
    /**
     * Define a página em uso na table
     */
    page: PropTypes.number,
    /**
     * Define a quantidade de itens por pagina
     */
    sizePerPage: PropTypes.number,
  }),
  /**
   * Props adicionais para o componente BootstrapTable
   */
  bootstrapTableProps: PropTypes.object,
  /**
   * Exibe ou não o botão para imprimir a tabela
   */
  showPrint: PropTypes.bool,
  /**
   * Já realizou a primeira busca ou não
   */
  searched: PropTypes.bool,
  /**
   * Permite que a tabela seja exibida mesmo sem resultados
   */
  allowEmpty: PropTypes.bool,
  /**
   * Titulo da mensagem para o caso de nao existirem registros na tabela
   */
  notFoundTitle: PropTypes.string,
  /**
   * Subtítulo da mensagem para o caso de nao existirem registros na tabela (não exibe a tabela)
   */
  notFoundSubtitle: PropTypes.string,
  /**
   * Titulo da mensagem para o caso de nao existirem registros na tabela (não exibe a tabela)
   */
  noResultTitle: PropTypes.string,
  /**
   * Subtítulo da mensagem para o caso de nao existirem registros na tabela
   */
  noResultSubtitle: PropTypes.string,
  /**
   * Utiliza um estilo a mais para o caso da table ter footer
   */
  hasFooter: PropTypes.string,
  /**
   * Componente de ações extras que será exibido após o título da tabela
   */
  extraActions: PropTypes.object,
};

export default ToolkitTable;
